import { useEffect, useRef, useState } from 'react';
import { LifeBuoy, Loader2 } from 'lucide-react';
import {
  performCleanReload,
  isConnectionHealthy,
  hasReachedCleanReloadCap,
  getCleanReloadAttempts,
  MAX_CLEAN_RELOAD_ATTEMPTS,
} from '@/lib/cleanReload';
import { createTicketAuto } from '@/lib/createTicket';
import { TicketCreatedDialog } from '@/components/support/TicketCreatedDialog';

interface Props {
  rolesLoading: boolean;
  accessLoading: boolean;
  profileLoading: boolean;
}

/**
 * Loading indicator with progressive escalation. Tres etapas:
 *
 *   0–10s   spinner básico (silencio)
 *   10–25s  spinner + "tomando más de lo normal" hint
 *   25s+    panel con diagnóstico + botón "Recarga limpia" o ticket
 *
 * Si CUALQUIERA de los flags cambia durante la espera, reseteamos
 * el timer — eso significa que hay progreso y no hay que escalar.
 *
 * Cuando llegamos al stage 2:
 *   - Si offline + sin caché → mensaje "necesitas internet la primera vez"
 *   - Si llegamos al cap (3 intentos previos de recarga limpia) →
 *     ofrecemos solo crear ticket P1 (no más botón de recarga, ya
 *     no es algo que el usuario pueda resolver)
 *   - Si NO hemos llegado al cap → mostramos botón de recarga limpia
 *     con guardia de conexión: pingeamos Supabase antes de proceder
 *     para no dejar al cliente sin caché Y sin red.
 */
export function StuckLoader({ rolesLoading, accessLoading, profileLoading }: Props) {
  const [stage, setStage] = useState<0 | 1 | 2>(0);
  const [resetting, setResetting] = useState(false);
  const [healthChecking, setHealthChecking] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [ticketCreating, setTicketCreating] = useState(false);
  const [ticketNumber, setTicketNumber] = useState<string | null>(null);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);

  const flagsSnapshot = useRef(`${rolesLoading}:${accessLoading}:${profileLoading}`);
  const lastProgressAt = useRef(Date.now());

  useEffect(() => {
    const next = `${rolesLoading}:${accessLoading}:${profileLoading}`;
    if (next !== flagsSnapshot.current) {
      flagsSnapshot.current = next;
      lastProgressAt.current = Date.now();
      setStage(0);
    }
  }, [rolesLoading, accessLoading, profileLoading]);

  useEffect(() => {
    const t = setInterval(() => {
      const elapsed = Date.now() - lastProgressAt.current;
      if (elapsed >= 25_000) setStage(2);
      else if (elapsed >= 10_000) setStage((s) => (s < 1 ? 1 : s));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const handleCleanReload = async () => {
    if (resetting || healthChecking) return;
    setHealthError(null);
    setHealthChecking(true);
    try {
      const healthy = await isConnectionHealthy();
      if (!healthy) {
        setHealthError(
          'Tu conexión parece inestable. Mejor inténtalo cuando tengas mejor señal — sin internet la recarga limpia te dejaría peor.',
        );
        return;
      }
      setResetting(true);
      await performCleanReload();
      // performCleanReload llama window.location.replace → no llegamos acá
    } catch {
      setResetting(false);
      setHealthError('No pudimos hacer la recarga limpia. Crea un ticket si el problema persiste.');
    } finally {
      setHealthChecking(false);
    }
  };

  const handleCreateTicket = async () => {
    if (ticketCreating) return;
    setTicketCreating(true);
    const attempts = getCleanReloadAttempts();
    const result = await createTicketAuto({
      title: attempts >= MAX_CLEAN_RELOAD_ATTEMPTS
        ? '[Crítico] App no carga tras múltiples recargas limpias'
        : '[Bloqueo] Pantalla de carga atorada',
      description: attempts >= MAX_CLEAN_RELOAD_ATTEMPTS
        ? `El cliente intentó recargar limpio ${attempts} veces y la app sigue sin cargar. Esto requiere intervención del equipo.`
        : 'La pantalla de carga llegó al límite de 25s sin avanzar. El cliente reportó desde el StuckLoader.',
      category: 'bug',
      captureScreenshot: false,
      extra: {
        cleanReloadAttempts: attempts,
        rolesLoading, accessLoading, profileLoading,
        navigatorOnLine: typeof navigator !== 'undefined' ? navigator.onLine : null,
      },
    });
    setTicketCreating(false);
    if (result) {
      setTicketNumber(result.ticket_number);
      setTicketDialogOpen(true);
    }
  };

  const isFirstTimeOffline = stage === 2 && typeof navigator !== 'undefined' && !navigator.onLine;
  const reachedCap = hasReachedCleanReloadCap();
  const attempts = getCleanReloadAttempts();

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-10 gap-6">
      <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />

      {stage === 1 && (
        <p className="text-xs text-muted-foreground text-center max-w-xs">
          Esto está tardando más de lo normal. Tu conexión parece lenta…
        </p>
      )}

      {stage === 2 && isFirstTimeOffline && (
        <div className="max-w-md w-full bg-card border-2 border-amber-500/40 rounded-xl p-5 space-y-3">
          <h2 className="text-base font-black uppercase tracking-tight">
            Necesitas internet la primera vez
          </h2>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Para usar la app sin internet necesitas haber entrado al menos
            una vez con conexión. Conéctate a Wi-Fi o datos móviles y vuelve
            a abrir la app.
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Si ya tenías la app funcionando antes, espera unos segundos a
            que se recupere la señal.
          </p>
        </div>
      )}

      {stage === 2 && !isFirstTimeOffline && reachedCap && (
        <div className="max-w-md w-full bg-card border-2 border-destructive/50 rounded-xl p-5 space-y-3">
          <h2 className="text-base font-black uppercase tracking-tight">
            Esto requiere nuestra ayuda
          </h2>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Ya intentamos recargar tu app {attempts} veces y sigue sin
            funcionar. Esto significa que necesitamos revisarlo nosotros —
            no es algo que puedas resolver tú.
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Crea un ticket y te respondemos lo antes posible. Tu reporte
            incluye los detalles técnicos automáticamente.
          </p>
          <button
            onClick={handleCreateTicket}
            disabled={ticketCreating || ticketNumber !== null}
            className="w-full px-4 py-3 rounded-xl bg-accent text-accent-foreground font-black uppercase tracking-wider text-sm hover:bg-accent/90 disabled:opacity-60 transition-colors min-h-[48px] flex items-center justify-center gap-2"
          >
            {ticketCreating
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Creando reporte…</>
              : ticketNumber
                ? <>✓ Reporte {ticketNumber} creado</>
                : <><LifeBuoy className="w-4 h-4" /> Crear ticket urgente</>
            }
          </button>
        </div>
      )}

      {stage === 2 && !isFirstTimeOffline && !reachedCap && (
        <div className="max-w-md w-full bg-card border-2 border-border rounded-xl p-5 space-y-4">
          <div>
            <h2 className="text-base font-black uppercase tracking-tight">
              Esto está tardando demasiado
            </h2>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
              Llevamos más de 25 segundos intentando cargar tu información.
              Lo que faltó:
            </p>
          </div>

          <ul className="text-xs space-y-1 bg-secondary/40 rounded-lg p-3">
            <li className="flex items-center justify-between">
              <span>Tus permisos de acceso</span>
              <span>{rolesLoading ? '⏳ Cargando' : '✅ Listo'}</span>
            </li>
            <li className="flex items-center justify-between">
              <span>Tu cuenta y suscripción</span>
              <span>{accessLoading ? '⏳ Cargando' : '✅ Listo'}</span>
            </li>
            <li className="flex items-center justify-between">
              <span>Tu perfil y rutina</span>
              <span>{profileLoading ? '⏳ Cargando' : '✅ Listo'}</span>
            </li>
          </ul>

          <p className="text-xs text-muted-foreground leading-relaxed">
            Puedes intentar una recarga limpia: borra los datos guardados
            en tu teléfono y vuelve a empezar de cero. Necesita internet
            para funcionar.
            {attempts > 0 && (
              <span className="block mt-1.5 text-amber-700 dark:text-amber-400">
                Intento {attempts + 1} de {MAX_CLEAN_RELOAD_ATTEMPTS}.
              </span>
            )}
          </p>

          {healthError && (
            <p className="text-xs text-destructive bg-destructive/10 rounded-lg p-2.5 leading-relaxed">
              {healthError}
            </p>
          )}

          <button
            onClick={handleCleanReload}
            disabled={resetting || healthChecking}
            className="w-full px-4 py-3 rounded-xl bg-accent text-accent-foreground font-black uppercase tracking-wider text-sm hover:bg-accent/90 disabled:opacity-60 transition-colors min-h-[48px] flex items-center justify-center gap-2"
          >
            {healthChecking
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Verificando conexión…</>
              : resetting
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Limpiando…</>
                : 'Probar recarga limpia'
            }
          </button>

          <button
            onClick={handleCreateTicket}
            disabled={ticketCreating}
            className="w-full px-4 py-2 rounded-lg text-muted-foreground hover:text-foreground font-medium text-[11px] uppercase tracking-wider transition-colors flex items-center justify-center gap-1.5"
          >
            <LifeBuoy className="w-3.5 h-3.5" />
            {ticketCreating ? 'Creando reporte…' : 'O reportar a soporte'}
          </button>
        </div>
      )}

      <TicketCreatedDialog
        open={ticketDialogOpen}
        onOpenChange={setTicketDialogOpen}
        ticketNumber={ticketNumber}
      />
    </div>
  );
}
